import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Star, Heart, Clock, ChefHat, Edit, Trash2, User } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RecipeCard = (props) => {
  const { t } = useTranslation();
  return RecipeCardOriginal(props);
};

const RecipeCardOriginal = ({ recipe, onRecipeUpdate }) => {
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);

  const handleClick = () => {
    navigate(`/recipe/${recipe.id}`);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    navigate(`/edit/${recipe.id}`);
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    try {
      setDeleting(true);
      await axios.delete(`${API}/recipes/${recipe.id}`);
      toast.success('Receita deletada!');
      if (onRecipeUpdate) {
        onRecipeUpdate();
      }
    } catch (error) {
      console.error('Error deleting recipe:', error);
      toast.error('Erro ao deletar receita');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card 
      data-testid={`recipe-card-${recipe.id}`}
      onClick={handleClick}
      className="group cursor-pointer overflow-hidden rounded-2xl border-2 border-amber-100 hover:border-orange-400 transition-all duration-300 hover:shadow-xl bg-white"
    >
      {/* Image */}
      <div className="relative h-56 overflow-hidden bg-gradient-to-br from-amber-100 to-orange-100">
        {recipe.image_url ? (
          <img 
            src={recipe.image_url} 
            alt={recipe.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ChefHat className="w-16 h-16 text-amber-300" />
          </div>
        )}
        
        {/* Category Badge */}
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          <Badge className="bg-white/90 backdrop-blur-sm text-amber-900 border-amber-200 px-3 py-1">
            {recipe.category}
          </Badge>
          {recipe.user_name && (
            <Badge className="bg-orange-600/90 backdrop-blur-sm text-white border-orange-700 px-3 py-1 flex items-center gap-1">
              <User className="w-3 h-3" />
              {recipe.user_name}
            </Badge>
          )}
        </div>

        {/* Action Buttons */}
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex gap-2">
          <Button
            data-testid={`edit-recipe-${recipe.id}`}
            onClick={handleEdit}
            size="icon"
            className="bg-white/90 backdrop-blur-sm hover:bg-white text-blue-600 hover:text-blue-700 shadow-lg h-9 w-9"
          >
            <Edit className="w-4 h-4" />
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                data-testid={`delete-recipe-${recipe.id}`}
                size="icon"
                className="bg-white/90 backdrop-blur-sm hover:bg-white text-red-600 hover:text-red-700 shadow-lg h-9 w-9"
                disabled={deleting}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent onClick={(e) => e.stopPropagation()}>
              <AlertDialogHeader>
                <AlertDialogTitle>Deletar Receita?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta ação não pode ser desfeita. A receita será permanentemente deletada.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDelete}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Deletar
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-2xl font-bold text-amber-900 mb-2 line-clamp-2 group-hover:text-orange-600 transition-colors">
          {recipe.title}
        </h3>
        
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
          {recipe.description}
        </p>

        {/* Meta Info */}
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center gap-4">
            {recipe.nutrition?.per_serving?.calories && (
              <div className="flex items-center gap-1 font-semibold text-orange-600">
                <span>{recipe.nutrition.per_serving.calories} cal</span>
              </div>
            )}
            
            {recipe.prep_time && (
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{recipe.prep_time + (recipe.cook_time || 0)} min</span>
              </div>
            )}
            
            {recipe.rating_average > 0 && (
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                <span>{recipe.rating_average.toFixed(1)}</span>
              </div>
            )}
          </div>

          {recipe.favorites_count > 0 && (
            <div className="flex items-center gap-1 text-red-500">
              <Heart className="w-4 h-4 fill-red-500" />
              <span>{recipe.favorites_count}</span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default RecipeCard;